import { useTranslation } from 'react-i18next'
import { useContext, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Breadcrumbs, Typography, ListItemButton, TextField, InputAdornment, IconButton } from '@mui/material'

import { FormError, Payment } from '../types'
import { UserContext } from '../contexts'
import { numberWithCommas } from '../utils'
import Chip from '../components/common/chip'
import HashIcon from '../assets/icons/hash-icon.svg'
import NextIcon from '../assets/icons/next-icon.svg'
import TicketIcon from '../assets/icons/ticket-icon.svg'
import SearchIcon from '../assets/icons/search-icon.svg'
import { textFieldWhiteBg } from '../theme/custom-styles'
import BatchesIcon from '../assets/icons/batches-icon.svg'
import CalendarIcon from '../assets/icons/calendar-icon.svg'
import DownloadIcon from '../assets/icons/download-icon.svg'
import MoneyGreenIcon from '../assets/icons/money-icon-green.svg'
import CheckIconGreen from '../assets/icons/check-icon-green.svg'
import TransactionsIcon from '../assets/icons/transactions-icon.svg'
import { ReportSquareContainer } from '../components/reports-brief-management/report-square-container'
import DocumentPaymentListItem from '../components/document-payments-details-management/document-payment-list-item'
import Pagination from '../components/common/pagination'

const BankingPartnerDetailsManagement = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userSession } = useContext(UserContext)
  const componentDidMount = useRef<boolean>(false)
  const { t } = useTranslation(['reports-management'])

  const [errors, setErrors] = useState<FormError>({})
  const [transactionToSearch /* setTransactionToSearch */] = useState<string>('')
  const [transactionsData /* setTransactionsData */] = useState<Array<any>>([])
  const [, /* openPaymentsHistoryModal */ setOpenPaymentsHistoryModal] = useState<boolean>(false)

  const handleSetTransactionsDataToSearch = (transactionToSearchData: string, showError: boolean) => {
    console.log(transactionToSearchData, showError)
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        aria-label="breadcrumb"
      >
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate('/customer-companies')}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('bankingPartnerDetails.clientsManagement')}
          </Typography>
        </ListItemButton>
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate(`/${location.state.previousPath}`)}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('bankingPartnerDetails.clientProfile')}
          </Typography>
        </ListItemButton>
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('bankingPartnerDetails.title', { bankingPartnerName: location.state.bankingPartnerName, companyName: location.state.companyName })}
        </Typography>
      </Breadcrumbs>
      <div
        style={{ marginTop: '20px' }}
        className="reports-row-container"
      >
        <ReportSquareContainer
          showMainIcon
          showCheckIcon
          iconWidth="32px"
          icon={BatchesIcon}
          showButtonGoDetails
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          className="reports-square-payments"
          mainData={`${numberWithCommas(0)}`}
          handleButtonGoDetailsAction={() => navigate('/payments-to-review', { state: { previousPath: `customer-company-payments/${userSession?.belongsTo}` } })}
          reportName={t('bankingPartnerDetails.topModules.batches', { ns: 'reports-management' })}
          detailsButtonText={t('bankingPartnerDetails.topModules.batchesButtonText', { ns: 'reports-management' })}
        />
        <ReportSquareContainer
          showMainIcon
          showCheckIcon
          iconWidth="32px"
          showButtonGoDetails
          icon={TransactionsIcon}
          showSecondaryData={false}
          backgroundColor="#CCFBEF"
          className="reports-square-payments"
          mainData={`${numberWithCommas(0)}`}
          handleButtonGoDetailsAction={() => navigate('/payments-to-review', { state: { previousPath: `customer-company-payments/${userSession?.belongsTo}` } })}
          reportName={t('bankingPartnerDetails.topModules.transactions', { ns: 'reports-management' })}
          detailsButtonText={t('bankingPartnerDetails.topModules.transactionsButtonText', { ns: 'reports-management' })}
        />
      </div>
      <div
        style={{ marginTop: '24px' }}
        className="customer-company-fundings-history-container"
      >
        <div className="actions-bar">
          <div className="left-content">
            <div className="title">{t('bankingPartnerDetails.actionsBar.thereAreNoTransactionsToShow')}</div>
            {componentDidMount.current === true && transactionsData.length > 0 && (
              <div
                style={{ cursor: 'pointer' }}
                className="filter-button-container"
                onClick={() => setOpenPaymentsHistoryModal(true)}
              >
                <img
                  src={DownloadIcon}
                  alt="download-icon"
                />
                <div className="filter-button-text">{t('actionsBar.downloadReport', { ns: 'customer-details-management' })}</div>
              </div>
            )}
          </div>
          <TextField
            variant="standard"
            sx={textFieldWhiteBg}
            className="search-input"
            value={transactionToSearch}
            error={!!errors.transactionToSearch}
            helperText={errors.transactionToSearch}
            label={t('bankingPartnerDetails.searchField.placeholder')}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleSetTransactionsDataToSearch(transactionToSearch, true)
              }
            }}
            onChange={(event) => handleSetTransactionsDataToSearch(event.target.value, true)}
            onBlur={() => {
              if (transactionToSearch === '') setErrors({})
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label="toggle password visibility"
                    sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                  >
                    <img
                      src={SearchIcon}
                      alt="search-icon"
                    />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </div>
      </div>
      <div className="table-header-container">
        <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg table-header-text">{t('tableHeaders.date', { ns: 'customer-company-details-management' })}</div>
        <div className="customer-company-table-column customer-company-table-width-45 table-header-text">{t('tableHeaders.beneficiary', { ns: 'customer-company-details-management' })}</div>
        <div className="customer-company-table-column customer-company-table-width-15 table-header-text customer-company-display-none-lg">{t('tableHeaders.amount', { ns: 'customer-company-details-management' })}</div>
        <div className="customer-company-table-column customer-company-table-width-20 table-header-text customer-company-display-none-sm">
          {t('tableHeaders.referenceNumber', { ns: 'customer-company-details-management' })}
        </div>
        <div className="customer-company-table-column customer-company-table-width-10 table-header-text">{t('tableHeaders.status', { ns: 'customer-company-details-management' })}</div>
      </div>
      <div className="documents-list-container">
        <DocumentPaymentListItem
          // key={index}
          showDateData
          showCustomerData
          paymentData={{} as Payment}
          handleOpenPaymentDetailsModal={(selectedPaymentData: Partial<Payment>) => {
            console.log('selectedPaymentData', selectedPaymentData)
            /* if (selectedPaymentData.status === 'waiting-for-resolution') {
            navigate(`/payment-to-review-details/${String(selectedPaymentData._id)}`, { state: { previousPath: `customer-company-payments-history/${userSession.belongsTo}` } })
            return
          }

          setOpenPaymentDetailsModal(true)
          setSelectedPaymentDetails(selectedPaymentData) */
          }}
        />
      </div>
      <Pagination
        pageSize={50}
        currentPage={1}
        handlePaginationPageChange={(page: number) => {
          console.log(page)
          /* setTimeout(() => {
                        setScrollToTop(true)
                        setCurrentListPage(page)
                        currentListPageRef.current = page
                      }, 100) */
        }}
        handleChangePageSize={(pageSizeToSet: string) => {
          console.log(pageSizeToSet)
          /* setScrollToTop(true)
                      setPageSize(Number(pageSizeToSet)) */
        }}
        totalCount={0}
      />
      <div className="table-header-container table-header-batches">
        <div
          id="last-batches-header-date"
          className="text-header table-header-width-15"
        >
          {t('lastBatches.tableHeaders.date', { ns: 'reports-management' }).toUpperCase()}
        </div>
        <div
          id="last-batches-header-name"
          className="text-header table-header-width-15"
        >
          {t('lastBatches.tableHeaders.nameOfOperation', { ns: 'reports-management' }).toUpperCase()}
        </div>
        <div
          id="last-batches-header-id"
          className="text-header table-header-width-15"
        >
          {t('lastBatches.tableHeaders.batchId', { ns: 'reports-management' }).toUpperCase()}
        </div>
        <div
          id="last-batches-header-partner"
          className="text-header table-header-width-15"
        >
          {t('lastBatches.tableHeaders.bankingPartner', { ns: 'reports-management' }).toUpperCase()}
        </div>
        <div
          id="last-batches-header-num-payments"
          className="text-header table-header-width-15"
        >
          {t('lastBatches.tableHeaders.numberOfPayments', { ns: 'reports-management' }).toUpperCase()}
        </div>
        <div
          id="last-batches-header-amount"
          className="text-header table-header-width-15"
        >
          {t('lastBatches.tableHeaders.amount', { ns: 'reports-management' }).toUpperCase()}
        </div>
        <div
          id="last-batches-header-status"
          className="text-header table-header-width-15"
        >
          {t('lastBatches.tableHeaders.status', { ns: 'reports-management' }).toUpperCase()}
        </div>
      </div>
      <div className="documents-list-container">
        <div
          /* key={index} */
          className="customer-details-list-item-data-container"
        >
          <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
            <div className="customer-details-list-item-icon">
              <img
                src={CalendarIcon}
                alt="calendar-icon"
              />
            </div>
            <div className="customer-details-list-item-text">{/* format(new Date(batchData.createdAt), 'dd/MM/yyyy | HH:mm:ss') */}</div>
          </div>
          <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
            <div className="customer-details-list-item-text">{/* batchData.originalName.replace(/\.[^.]*$/, '').toUpperCase() */}</div>
          </div>
          <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
            <div className="customer-details-list-item-icon">
              <img
                src={TicketIcon}
                alt="ticket-icon"
              />
            </div>
            <div className="customer-details-list-item-text">{/* String(batchData._id).slice(-8) */}</div>
          </div>
          <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
            <div className="customer-details-list-item-icon">
              <img
                src={CheckIconGreen}
                alt="check-icon-green"
              />
            </div>
            <div className="customer-details-list-item-text">
              {/* batchData.cobrePaymentReference && Array.isArray(batchData.cobrePaymentReference) && batchData.cobrePaymentReference.length > 0 ? 'COBRE' : 'MONNET' */}
            </div>
          </div>
          <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
            <div className="customer-details-list-item-icon">
              <img
                src={HashIcon}
                alt="hash-icon"
              />
            </div>
            <div className="customer-details-list-item-text">{/* batchData.totalOfNoveltiesReviewed */}</div>
          </div>
          <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
            <div className="customer-details-list-item-icon">
              <img
                src={MoneyGreenIcon}
                alt="money-icon"
              />
            </div>
            <div className="customer-details-list-item-text">{/* `${numberWithCommas(batchData.batchAmount)} COP` */}</div>
          </div>
          <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
            <Chip
              status="success"
              label="Completado"
            />
          </div>
        </div>
      </div>
      <Pagination
        pageSize={50}
        currentPage={1}
        handlePaginationPageChange={(page: number) => {
          console.log(page)
          /* setTimeout(() => {
                        setScrollToTop(true)
                        setCurrentListPage(page)
                        currentListPageRef.current = page
                      }, 100) */
        }}
        handleChangePageSize={(pageSizeToSet: string) => {
          console.log(pageSizeToSet)
          /* setScrollToTop(true)
                      setPageSize(Number(pageSizeToSet)) */
        }}
        totalCount={0}
      />
    </div>
  )
}

export default BankingPartnerDetailsManagement
